import { computed } from 'vue';
import { setDialogConfirmation } from 'dashboard/components/ui/commslayer';

/**
 * Composable for handling form navigation guards
 * Provides route leave guard and browser beforeunload handling
 *
 * @param {Object} options - Configuration options
 * @param {import('vue').Ref<boolean>|import('vue').ComputedRef<boolean>} options.hasChanges - Reactive value that returns true if form has unsaved changes
 * @param {import('vue').Ref<boolean>} [options.isSubmitting] - Optional reactive value that tracks submission state
 * @param {string} [options.title='Discard unsaved changes?'] - Dialog title
 * @param {string} [options.message='Your changes will be lost.'] - Dialog message
 * @param {string} [options.confirmText='Discard'] - Confirm button text
 * @param {string} [options.cancelText='Keep editing'] - Cancel button text
 *
 * @returns {Object} Guard handlers
 * @returns {Function} routeLeaveGuard - Function to use in beforeRouteLeave
 * @returns {Function} beforeUnloadHandler - Function to use in window beforeunload event
 *
 * @example
 * // Recommended: Use with useForm (hasChanges is built-in)
 * <script setup>
 * import { useForm, useFormGuard } from 'dashboard/composables';
 *
 * const { form, hasChanges, isSubmitting } = useForm({
 *   initialValues: {
 *     title: '',
 *     description: '',
 *   },
 *   onSubmit: async (values) => {
 *     // submit logic
 *   },
 * });
 *
 * const { routeLeaveGuard, beforeUnloadHandler } = useFormGuard({
 *   hasChanges,      // from useForm
 *   isSubmitting,    // from useForm
 * });
 *
 * defineExpose({ routeLeaveGuard });
 * </script>
 *
 * @example
 * // Alternative: Manual hasChanges (without useForm)
 * <script setup>
 * import { ref, computed } from 'vue';
 * import { useFormGuard } from 'dashboard/composables';
 *
 * const title = ref('');
 * const description = ref('');
 * const isSubmitting = ref(false);
 *
 * const hasChanges = computed(() => !!(title.value || description.value));
 *
 * const { routeLeaveGuard, beforeUnloadHandler } = useFormGuard({
 *   hasChanges,
 *   isSubmitting
 * });
 *
 * defineExpose({ routeLeaveGuard });
 * </script>
 *
 * // In component with <script> (Options API)
 * <script>
 * export default {
 *   beforeRouteLeave(...args) {
 *     return this.routeLeaveGuard(...args);
 *   },
 *   mounted() {
 *     window.addEventListener('beforeunload', this.beforeUnloadHandler);
 *   },
 *   beforeDestroy() {
 *     window.removeEventListener('beforeunload', this.beforeUnloadHandler);
 *   },
 * };
 * </script>
 */
export function useFormGuard({
  hasChanges,
  isSubmitting,
  title = 'Discard unsaved changes?',
  message = 'Your changes will be lost.',
  confirmText = 'Discard',
  cancelText = 'Keep editing',
} = {}) {
  if (!hasChanges) {
    throw new Error(
      'useFormGuard: "hasChanges" is required. It should be a ref or computed that returns a boolean.'
    );
  }

  /**
   * Route leave guard function
   * Use this in beforeRouteLeave navigation guard
   *
   * @param {Object} _to - Target route
   * @param {Object} _from - Current route
   * @param {Function} next - Navigation resolver function
   */
  const routeLeaveGuard = (_to, _from, next) => {
    const shouldWarn = computed(() => {
      const hasUnsavedChanges = hasChanges.value;
      const isCurrentlySubmitting = isSubmitting ? isSubmitting.value : false;
      return hasUnsavedChanges && !isCurrentlySubmitting;
    }).value;

    if (shouldWarn) {
      setDialogConfirmation({
        title,
        message,
        confirmText,
        cancelText,
        onConfirm: () => {
          next(); // Allow navigation
        },
        onCancel: () => {
          next(false); // Cancel navigation
        },
      });
    } else {
      next(); // No changes, allow navigation
    }
  };

  /**
   * Browser beforeunload handler
   * Add this in mounted() and remove in beforeDestroy()
   *
   * @param {BeforeUnloadEvent} e - Browser beforeunload event
   */
  const beforeUnloadHandler = e => {
    const shouldWarn = computed(() => {
      const hasUnsavedChanges = hasChanges.value;
      const isCurrentlySubmitting = isSubmitting ? isSubmitting.value : false;
      return hasUnsavedChanges && !isCurrentlySubmitting;
    }).value;

    if (shouldWarn) {
      e.preventDefault();
      e.returnValue = ''; // Chrome requires returnValue to be set
    }
  };

  return {
    routeLeaveGuard,
    beforeUnloadHandler,
  };
}
